import { useState, FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const contactInfo = [
  {
    icon: 'ri-map-pin-2-line',
    label: 'Our Address',
    lines: ['235 Court Road', 'Kano, Nigeria'],
  },
  {
    icon: 'ri-phone-line',
    label: 'Phone Numbers',
    lines: ['+234 809 955 4455', '+234 706 090 8252'],
  },
  {
    icon: 'ri-mail-line',
    label: 'Email Us',
    lines: ['info@ikiruepilepsy.org'],
  },
  {
    icon: 'ri-time-line',
    label: 'Office Hours',
    lines: ['Mon – Fri: 8:00am – 5:00pm', 'Sat: 9:00am – 1:00pm'],
  },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
      });
      await fetch('https://readdy.ai/api/form/d7d87hem3fsfopf23nvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-40 pb-20 bg-[#1A0A1E] overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-[#4B0082] via-[#C084FC] to-[#7B2D8B]"></div>
        <div className="absolute top-20 right-0 w-[500px] h-[500px] rounded-full bg-[#7B2D8B]/15 blur-3xl pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-10 relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-purple-400/30 bg-purple-900/30 backdrop-blur-sm mb-8">
            <span className="w-2 h-2 rounded-full bg-[#C084FC] animate-pulse"></span>
            <span className="text-purple-200 text-sm tracking-wide">We&apos;d Love to Hear From You</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-extrabold text-white leading-tight mb-6">
            Get in <span className="text-[#C084FC]">Touch</span>
          </h1>
          <p className="text-gray-400 text-lg max-w-xl leading-relaxed">
            Whether you want to partner with us, support our mission, or simply learn more about epilepsy care in Africa — we&apos;re here and ready to connect.
          </p>
        </div>
      </section>

      {/* Contact Cards + Form */}
      <section className="py-20 bg-[#FAF7FC]">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

            {/* Left: Contact Info */}
            <div className="flex flex-col gap-6">
              {contactInfo.map((item) => (
                <div key={item.label} className="bg-white rounded-2xl p-6 border border-purple-100 flex gap-4 items-start">
                  <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#7B2D8B]/10 shrink-0">
                    <i className={`${item.icon} text-[#7B2D8B] text-xl`}></i>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-[#7B2D8B] mb-2">{item.label}</div>
                    {item.lines.map((line) => (
                      <div key={line} className="text-gray-700 text-sm font-medium">{line}</div>
                    ))}
                  </div>
                </div>
              ))}

              {/* Social */}
              <div className="bg-[#7B2D8B] rounded-2xl p-6">
                <div className="text-xs font-semibold uppercase tracking-widest text-purple-200 mb-4">Follow Us</div>
                <div className="flex gap-3 flex-wrap">
                  {[
                    { icon: 'ri-facebook-fill', label: 'Facebook' },
                    { icon: 'ri-twitter-x-fill', label: 'Twitter/X' },
                    { icon: 'ri-instagram-line', label: 'Instagram' },
                    { icon: 'ri-linkedin-fill', label: 'LinkedIn' },
                  ].map((s) => (
                    <a
                      key={s.icon}
                      href="#"
                      rel="nofollow"
                      aria-label={s.label}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/30 text-white transition-colors cursor-pointer"
                    >
                      <i className={`${s.icon} text-sm`}></i>
                    </a>
                  ))}
                </div>
                <p className="text-purple-200 text-xs mt-4 leading-relaxed">
                  Stay updated on our programs, events, and impact stories across Nigeria and Africa.
                </p>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-2 bg-white rounded-3xl p-10 border border-purple-100">
              {submitted ? (
                <div className="flex flex-col items-center justify-center h-full py-20 text-center">
                  <div className="w-20 h-20 flex items-center justify-center rounded-full bg-[#7B2D8B]/10 mb-6">
                    <i className="ri-checkbox-circle-line text-[#7B2D8B] text-4xl"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Message Sent!</h3>
                  <p className="text-gray-500 max-w-sm leading-relaxed mb-8">
                    Thank you for reaching out. Our team will get back to you within 1–2 business days.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ name: '', email: '', subject: '', message: '' }); }}
                    className="px-6 py-3 rounded-full bg-[#7B2D8B] text-white text-sm font-semibold hover:bg-[#6a2578] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Send Us a Message</h2>
                  <p className="text-gray-500 text-sm mb-8">Fill in the form below and we&apos;ll respond as soon as possible.</p>

                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">Full Name *</label>
                        <input
                          type="text"
                          name="name"
                          value={form.name}
                          onChange={handleChange}
                          required
                          placeholder="e.g. Amara Okonkwo"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#7B2D8B] transition-colors"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={form.email}
                          onChange={handleChange}
                          required
                          placeholder="you@example.com"
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#7B2D8B] transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">Subject *</label>
                      <select
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:border-[#7B2D8B] transition-colors cursor-pointer"
                      >
                        <option value="">Select a subject...</option>
                        <option value="General Enquiry">General Enquiry</option>
                        <option value="Partnership Opportunity">Partnership Opportunity</option>
                        <option value="Donation & Funding">Donation &amp; Funding</option>
                        <option value="Volunteer Interest">Volunteer Interest</option>
                        <option value="Media & Press">Media &amp; Press</option>
                        <option value="Patient Support">Patient Support</option>
                        <option value="Healthcare Professional">Healthcare Professional</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">
                        Message * <span className="text-gray-400 normal-case font-normal tracking-normal">({form.message.length}/500)</span>
                      </label>
                      <textarea
                        name="message"
                        value={form.message}
                        onChange={handleChange}
                        required
                        maxLength={500}
                        rows={6}
                        placeholder="Tell us how we can help or how you'd like to get involved..."
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#7B2D8B] transition-colors resize-none"
                      />
                      {form.message.length > 500 && (
                        <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>
                      )}
                    </div>

                    {error && (
                      <div className="flex items-center gap-2 text-red-500 text-sm">
                        <i className="ri-error-warning-line"></i>
                        <span>{error}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={submitting || form.message.length > 500}
                      className="w-full py-4 rounded-xl bg-[#7B2D8B] text-white font-semibold text-sm hover:bg-[#6a2578] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
                    >
                      {submitting ? (
                        <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                      ) : (
                        <><i className="ri-send-plane-line"></i> Send Message</>
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="bg-white pb-0">
        <div className="max-w-7xl mx-auto px-6 md:px-10 pb-0">
          <div className="rounded-3xl overflow-hidden h-80 border border-purple-100">
            <iframe
              title="IKIRU Epilepsy Foundation - 235 Court Road, Kano"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3903.0!2d8.5920!3d12.0022!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2s235+Court+Road%2C+Kano%2C+Nigeria!5e0!3m2!1sen!2sng!4v1"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            ></iframe>
          </div>
        </div>
      </section>

      {/* Quick links strip */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-8 rounded-2xl bg-[#FAF7FC] border border-purple-100">
            <div>
              <div className="font-bold text-gray-900 text-lg mb-1">Ready to make a difference?</div>
              <div className="text-gray-500 text-sm">Join IKIRU in transforming epilepsy care across Africa.</div>
            </div>
            <div className="flex gap-4 flex-wrap">
              <Link
                to="/get-involved"
                className="px-6 py-3 rounded-full bg-[#7B2D8B] text-white text-sm font-semibold hover:bg-[#6a2578] transition-colors cursor-pointer whitespace-nowrap"
              >
                Donate Now
              </Link>
              <Link
                to="/programs"
                className="px-6 py-3 rounded-full border-2 border-[#7B2D8B] text-[#7B2D8B] text-sm font-semibold hover:bg-[#7B2D8B]/5 transition-colors cursor-pointer whitespace-nowrap"
              >
                Our Programs
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}
